import React, { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import { LoadingPanel } from "../../components/layout/Loading";
import { Typography } from "@progress/kendo-react-common";
import { GridLayout, GridLayoutItem } from "@progress/kendo-react-layout";
import { Button } from "@progress/kendo-react-buttons";
import {
  Field,
  Form,
  FormElement,
  FormRenderProps,
} from "@progress/kendo-react-form";
import FormTextField from "../../components/formFields/FormTextField";
import {
  emailWOValueValidator,
  gstWOValueValidator,
  MobileNoValidator,
  panWOValueValidator,
  pincodeWOValueValidator,
  requiredValidator,
} from "../../components/formFields/CommonValidator";
import ButtonWithLoading from "../../components/common/ButtonWithLoading";
import {
  companyLogoUpload,
  createCompany,
  getCompanyByID,
  updateCompany,
} from "./services/company.services";
import { clearCompanyDetails } from "./companySlice";
import FormSelectionField from "../../components/formFields/FormSelectionField";
import { ICity } from "../city/cityModel";
import { findIndex } from "lodash";
import { getAllActiveCities } from "../city/services/city.services";
import { getAllActiveStates } from "../state/services/state.services";
import { getAllActiveCountries } from "../country/services/country.services";
import ShadowCard from "../../components/common/ShadowCard";
import { STATUSARRAY } from "../../_contstants/common";
import FormImageUpload from "../../components/formFields/FormImageUpload";
import FormCheckbox from "../../components/formFields/FormCheckbox";

interface CityChangeWatcherProps {
  formRenderProps: FormRenderProps;
  CityList: ICity[];
}

const CityChangeWatcher: React.FC<CityChangeWatcherProps> = ({
  CityList,
  formRenderProps,
}) => {
  const cityID = formRenderProps.valueGetter("CityID");

  useEffect(() => {
    const CityID = cityID ? cityID : undefined;
    const cityIndex = findIndex(CityList, { ID: CityID });
    if (cityIndex > -1) {
      const city = CityList[cityIndex];
      formRenderProps.onChange("StateID", { value: city.StateID });
      formRenderProps.onChange("CountryID", { value: city.CountryID });
    } else {
      formRenderProps.onChange("CityID", { value: "" });
      formRenderProps.onChange("StateID", { value: "" });
      formRenderProps.onChange("CountryID", { value: "" });
    }
  }, [cityID]);
  return null;
};
interface GSTChangeWatcherProps {
  formRenderProps: FormRenderProps;
}

const HandleGstNoChange: React.FC<GSTChangeWatcherProps> = ({
  formRenderProps,
}) => {
  const GSTNo = formRenderProps.valueGetter("GSTNo");
  const PANNo = formRenderProps.valueGetter("PANNo");
  const handleGstNoChange = () => {
    formRenderProps.onChange("GSTNo", { value: GSTNo.toUpperCase() });
  };
  const handlePanNoChange = () => {
    formRenderProps.onChange("PANNo", { value: PANNo.toUpperCase() });
  };

  useEffect(() => {
    handleGstNoChange();
  }, [GSTNo]);

  useEffect(() => {
    handlePanNoChange();
  }, [PANNo]);

  return null;
};

const CreateCompany: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const CompanyID = location.state?.CompanyID;
  const gridRef = useRef<any>(null);
  const dispatch = useAppDispatch();
  const loading = useAppSelector((state) => state.company.loading);
  const CompanyDetail = useAppSelector((state) => state.company.CompanyDetail);
  const CityList = useAppSelector((state) => state.city.CityList);
  const StateList = useAppSelector((state) => state.state.StateList);
  const CountryList = useAppSelector((state) => state.country.CountryList);
  const [formKey, setFormKey] = useState(1);

  useEffect(() => {
    setFormKey(formKey + 1);
  }, [CompanyDetail, CityList]);

  useEffect(() => {
    if (CompanyID) {
      dispatch(getCompanyByID(CompanyID));
    }
    return () => {
      dispatch(clearCompanyDetails());
    };
  }, [CompanyID]);

  useEffect(() => {
    dispatch(getAllActiveCities());
    dispatch(getAllActiveStates());
    dispatch(getAllActiveCountries());
  }, []);

  const handleSubmit = async (values: any) => {
    const ImageData = new FormData();
    const file =
      values?.CompanyLogo && typeof values?.CompanyLogo !== "string"
        ? values.CompanyLogo[0].getRawFile()
        : "";

    if (CompanyID) {
      try {
        const updatePayload = {
          ID: CompanyID,
          CompanyName: values.CompanyName ? values.CompanyName : "",
          Address: values.Address ? values.Address : "",
          CityID: values.CityID ? values.CityID : null,
          StateID: values.StateID ? values.StateID : null,
          CountryID: values.CountryID ? values.CountryID : null,
          OwnerName: values.OwnerName ? values.OwnerName : "",
          Pincode: values.Pincode ? values.Pincode?.toString() : "",
          MobileNo: values.MobileNo ? values.MobileNo : "",
          EmailID: values.EmailID ? values.EmailID : "",
          PANNo: values.PANNo ? values.PANNo : "",
          GSTNo: values.GSTNo ? values.GSTNo : "",
          AadharNo: values.AadharNo ? values.AadharNo : "",
          BankName: values.BankName ? values.BankName : "",
          BankBranch: values.BankBranch ? values.BankBranch : "",
          IsDefault: values.IsDefault ? values.IsDefault : false,
          BankAddress: values.BankAddress ? values.BankAddress : "",
          BankAccountNo: values.BankAccountNo ? values.BankAccountNo : "",
          BankIFSCCode: values.BankIFSCCode ? values.BankIFSCCode : "",
          IsActive: values.IsActive === 1,
        };
        const response = await dispatch(updateCompany(updatePayload));
        if (response?.meta?.requestStatus === "fulfilled") {
          if (values.CompanyLogo && typeof values.CompanyLogo !== "string") {
            ImageData.append("CompanyLogo", file);
            ImageData.append("ID", values?.ID ? values?.ID : "");

            await dispatch(companyLogoUpload(ImageData));
          }
          navigate("/company");
        }
      } catch (error) {
        console.error("Error in handleSubmit:", error);
        throw error;
      }
    } else {
      try {
        const insertPayload = {
          CompanyName: values.CompanyName ? values.CompanyName : "",
          Address: values.Address ? values.Address : "",
          CityID: values.CityID ? values.CityID : null,
          StateID: values.StateID ? values.StateID : null,
          CountryID: values.CountryID ? values.CountryID : null,
          OwnerName: values.OwnerName ? values.OwnerName : "",
          Pincode: values.Pincode ? values.Pincode?.toString() : "",
          MobileNo: values.MobileNo ? values.MobileNo : "",
          EmailID: values.EmailID ? values.EmailID : "",
          PANNo: values.PANNo ? values.PANNo : "",
          GSTNo: values.GSTNo ? values.GSTNo : "",
          AadharNo: values.AadharNo ? values.AadharNo : "",
          BankName: values.BankName ? values.BankName : "",
          BankBranch: values.BankBranch ? values.BankBranch : "",
          IsDefault: values.IsDefault ? values.IsDefault : false,
          BankAddress: values.BankAddress ? values.BankAddress : "",
          BankAccountNo: values.BankAccountNo ? values.BankAccountNo : "",
          BankIFSCCode: values.BankIFSCCode ? values.BankIFSCCode : "",
          IsActive: values.IsActive === 1,
        };
        const response = await dispatch(createCompany(insertPayload));
        if (response?.meta?.requestStatus === "fulfilled") {
          if (
            values?.CompanyLogo &&
            typeof values?.CompanyLogo !== "string" &&
            response?.payload?.Data?.CompanyID
          ) {
            ImageData.append("CompanyLogo", file);
            ImageData.append("ID", response?.payload?.Data?.CompanyID);

            await dispatch(companyLogoUpload(ImageData));
          }
          navigate(-1);
        }
      } catch (error) {
        console.error("Error in handleSubmit:", error);
        throw error;
      }
    }
  };

  return (
    <>
      {loading && <LoadingPanel gridRef={gridRef} />}
      <Form
        onSubmit={handleSubmit}
        initialValues={CompanyDetail}
        key={formKey}
        render={(formRenderProps: FormRenderProps) => (
          <FormElement>
            <ShadowCard style={{ padding: 12 }}>
              <GridLayout
                style={{ marginRight: 20 }}
                gap={{ rows: 0, cols: 10 }}
                cols={[
                  { width: "33.33%" },
                  { width: "33.33%" },
                  { width: "33.33%" },
                ]}
              >
                <GridLayoutItem colSpan={3}>
                  <Typography.h4>
                    {CompanyID ? "Update Company" : "Add Company"}
                  </Typography.h4>
                </GridLayoutItem>
                <GridLayoutItem>
                  <Field
                    name="CompanyName"
                    maxLength="100"
                    label="Company Name"
                    placeholder="Company Name"
                    astrike={true}
                    component={FormTextField}
                    validator={requiredValidator}
                  />
                </GridLayoutItem>
                <GridLayoutItem>
                  <Field
                    name="OwnerName"
                    maxLength="50"
                    placeholder="Owner Name"
                    label="Owner Name"
                    component={FormTextField}
                  />
                </GridLayoutItem>
                <GridLayoutItem>
                  <Field
                    name="MobileNo"
                    type="number"
                    placeholder="i.e. 9999999999"
                    label="Mobile No."
                    max="10"
                    component={FormTextField}
                    validator={MobileNoValidator}
                    astrike={true}
                  />
                </GridLayoutItem>
                <GridLayoutItem>
                  <Field
                    name="EmailID"
                    label="Email ID"
                    placeholder="i.e. example@example.com"
                    component={FormTextField}
                    validator={emailWOValueValidator}
                  />
                </GridLayoutItem>
                <GridLayoutItem colSpan={2}>
                  <Field
                    name="Address"
                    label="Address"
                    placeholder="Address"
                    component={FormTextField}
                  />
                </GridLayoutItem>
                <GridLayoutItem>
                  <Field
                    id={"CityID"}
                    name={"CityID"}
                    label={"City"}
                    placeholder="i.e. Ahmedabad"
                    component={FormSelectionField}
                    options={CityList.map((city: any) => {
                      return {
                        value: city.ID,
                        label: city.CityName,
                      };
                    })}
                  />
                </GridLayoutItem>
                <GridLayoutItem>
                  <Field
                    id={"StateID"}
                    name={"StateID"}
                    label={"State"}
                    placeholder="i.e. Gujarat"
                    component={FormSelectionField}
                    // disabled={true}
                    options={StateList.map((state: any) => {
                      return {
                        value: state.ID,
                        label: state.StateName,
                      };
                    })}
                  />
                </GridLayoutItem>
                <GridLayoutItem>
                  <Field
                    id={"CountryID"}
                    name={"CountryID"}
                    label={"Country"}
                    placeholder="i.e. India"
                    component={FormSelectionField}
                    // disabled={true}
                    options={CountryList.map((country: any) => {
                      return {
                        value: country.ID,
                        label: country.CountryName,
                      };
                    })}
                  />
                </GridLayoutItem>
                <GridLayoutItem>
                  <Field
                    name="Pincode"
                    type="number"
                    placeholder="Pin code"
                    label="Pin Code"
                    max={6}
                    component={FormTextField}
                    validator={pincodeWOValueValidator}
                  />
                </GridLayoutItem>
                <GridLayoutItem>
                  <Field
                    name="PANNo"
                    label="PAN No."
                    placeholder="PAN No."
                    maxLength={10}
                    validator={panWOValueValidator}
                    component={FormTextField}
                  />
                </GridLayoutItem>
                <GridLayoutItem>
                  <Field
                    name="GSTNo"
                    label="GST No."
                    placeholder="GST No."
                    validator={gstWOValueValidator}
                    component={FormTextField}
                    maxLength={15}
                    onChange={(e) =>
                      formRenderProps.onChange("GSTNo", {
                        value: e?.value?.toUpperCase(),
                      })
                    }
                  />
                </GridLayoutItem>
                <GridLayoutItem
                  style={{ display: "flex", flexDirection: "column" }}
                >
                  <Field
                    name="AadharNo"
                    label="Aadhar No."
                    type="number"
                    placeholder="Aadhar No."
                    max={12}
                    component={FormTextField}
                  />
                  <Field
                    id={"IsActive"}
                    name={"IsActive"}
                    label={"Status"}
                    placeholder="i.e. Active"
                    component={FormSelectionField}
                    validator={requiredValidator}
                    astrike={true}
                    options={STATUSARRAY?.map((status: any) => {
                      return {
                        value: status.value,
                        label: status.label,
                      };
                    })}
                  />
                </GridLayoutItem>
                <GridLayoutItem colSpan={2} rowSpan={2}>
                  <Field
                    name={`CompanyLogo`}
                    label="Company Logo (Upload square logo if possible)"
                    alternateName="Company Logo"
                    type="file"
                    accept="image/*"
                    component={FormImageUpload}
                    maxSize={0.5}
                  />
                </GridLayoutItem>
                <GridLayoutItem colSpan={3}>
                  <Typography.h5
                    style={{
                      textDecoration: "underline",
                      margin: "20px 0 0 0",
                    }}
                  >
                    Bank Details
                  </Typography.h5>
                </GridLayoutItem>
                <GridLayoutItem>
                  <Field
                    name="BankName"
                    label="Bank Name"
                    placeholder="Bank Name"
                    component={FormTextField}
                    maxLength={30}
                  />
                </GridLayoutItem>
                <GridLayoutItem colSpan={2}>
                  <Field
                    name="BankAddress"
                    label="Bank Address"
                    placeholder="Bank Address"
                    component={FormTextField}
                  />
                </GridLayoutItem>
                <GridLayoutItem>
                  <Field
                    name="BankAccountNo"
                    label="Bank Account No."
                    placeholder="Bank Account No."
                    component={FormTextField}
                    maxLength={15}
                  />
                </GridLayoutItem>
                <GridLayoutItem>
                  <Field
                    name="BankIFSCCode"
                    label="IFSC Code"
                    placeholder="IFSC Code"
                    component={FormTextField}
                    maxLength={15}
                    onChange={(e) =>
                      formRenderProps.onChange("BankIFSCCode", {
                        value: e?.value?.toUpperCase(),
                      })
                    }
                  />
                </GridLayoutItem>

                <GridLayoutItem>
                  <Field
                    name="BankBranch"
                    label="Bank Branch"
                    placeholder="Bank Branch"
                    component={FormTextField}
                    maxLength={30}
                  />
                </GridLayoutItem>
                <GridLayoutItem>
                  <Field
                    name="IsDefault"
                    label="Is Default Comapany"
                    component={FormCheckbox}
                    maxLength={30}
                  />
                </GridLayoutItem>

                <GridLayoutItem
                  colSpan={2}
                  style={{
                    textAlign: "end",
                    marginTop: "20px",
                    width: "100%",
                  }}
                >
                  <ButtonWithLoading
                    label={CompanyID ? "Update" : "Save"}
                    type="submit"
                    disabled={!formRenderProps.allowSubmit || loading}
                    loading={loading}
                  />

                  <Button
                    type="button"
                    fillMode={"outline"}
                    themeColor={"primary"}
                    style={{ marginLeft: 10 }}
                    onClick={() => navigate("/company")}
                  >
                    Cancel
                  </Button>
                </GridLayoutItem>
              </GridLayout>
            </ShadowCard>
            <HandleGstNoChange formRenderProps={formRenderProps} />
            <CityChangeWatcher
              CityList={CityList}
              formRenderProps={formRenderProps}
            />
          </FormElement>
        )}
      />
    </>
  );
};

export default CreateCompany;
